/**
 * The utilities in this file are mostly about refining GraphQL results
 * into something easier to work with.
 * e.g. removing edge/node structure, specifying defaults, computing summary data.
 */

import {
  Artifact,
  ArtifactAlias,
  Run,
  RunConnection,
} from '../../../generated/graphql';
import {isJobValidForPublicQueue} from '../publicQueue/configuration';
import {WANDB_PUBLIC_QUEUE_NAME} from '../publicQueue/constants';
import {ArtifactCollectionEdge, VersionInfo} from '../utilTypes';
const RE_VERSION = /^v(\d+)$/;

export type AliasInfo = {
  id: string;
  alias: string;
};

export type RunInfo = {
  id: string; // e.g. "UnVuOnYxOmRrNnB2N3JpOmZtbmlzdDpqYW1pZS1yYXNtdXNzZW4="
  name: string; // e.g. "dk6pv7ri"
  displayName: string; // e.g. "stellar-galaxy-30"
  createdAt: string;
  state: string;
};
export type UsedByInfo = {
  totalCount: number;
  runs: RunInfo[];
};

interface RunEdge {
  node: Run;
}
interface ArtifactEdge {
  node: Artifact;
}
export interface JobEdge {
  node: any;
}
export interface ArtifactInfo {
  id: string;
  description: string;
  aliases: AliasInfo[];
  usedBy: UsedByInfo;
}

export interface JobInfo {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  artifacts: ArtifactInfo[];
  versionInfo: VersionInfo;
  entityName: string;
  projectName: string;
}

export function getAliases(aliases: ArtifactAlias[]): AliasInfo[] {
  return aliases.map(a => {
    const {id, alias} = a;
    return {
      id,
      alias,
    };
  });
}

export function getVersionNumber(aliases: string[]): number {
  for (const alias of aliases) {
    const match = alias.match(RE_VERSION);
    if (match) {
      return parseInt(match[1], 10);
    }
  }
  throw new Error('No version number in artifact');
}

function getRuns(runEdges: RunEdge[]): RunInfo[] {
  return runEdges.map(runEdge => {
    const {id, name, displayName, state} = runEdge.node;
    // This conversion is because the type system thinks createdAt is a Date -
    // Scalars['DateTime'] in the generated code. It's actually a string coming
    // over the wire though, perhaps something is supposed to be converting it but isn't?
    // The toString call will force a string representation either way.
    const createdAt = runEdge.node.createdAt.toString();
    return {
      id,
      name,
      displayName: displayName ?? '',
      createdAt,
      state: state ?? 'unknown',
    };
  });
}

export function getUsedBy(usedBy: RunConnection): UsedByInfo {
  const {totalCount, edges} = usedBy;
  const runs = getRuns(edges);
  return {
    totalCount,
    runs,
  };
}

export function getArtifactInfo(artifactEdges: ArtifactEdge[]): ArtifactInfo[] {
  return artifactEdges.map(artifactEdge => {
    const {id, description, aliases, usedBy} = artifactEdge.node;
    return {
      id,
      description: description ?? '',
      aliases: getAliases(aliases),
      usedBy: getUsedBy(usedBy),
    };
  });
}

export function getMostRecentRunCreated(usedBy: UsedByInfo): string | null {
  let mostRecentRun = null;
  for (const run of usedBy.runs) {
    if (!mostRecentRun || run.createdAt > mostRecentRun) {
      mostRecentRun = run.createdAt;
    }
  }
  return mostRecentRun;
}

/**
 * There are some complexities with job versions.
 * - Users can delete the "latest" alias. (Though perhaps we should disallow this.)
 * - Users can delete individual job versions.
 * This method loops through all of the version information to summarize
 * things like total number of runs across all versions.
 *
 * Caller can override the runTarget with defaultJobVersion, though
 * that will be ignored if it isn't valid.
 */
export function getVersionInfo(
  artifacts: ArtifactInfo[],
  defaultJobVersion?: string
): VersionInfo {
  let defaultFound = false;
  const info: VersionInfo = {
    hasLatest: false,
    latest: null,
    highest: null,
    runTarget: null, // 'latest' or highest if no latest
    runTargetNum: null,
    runsCount: 0,
    mostRecentRun: null, // Timestamp of most recent run
  };
  const versionNumbers = [];
  for (const version of artifacts) {
    // Extract just the text part of aliases (ignore IDs)
    const aliases = version.aliases.map(a => a.alias);
    const versionNumber = getVersionNumber(aliases);
    versionNumbers.push(versionNumber);
    if (aliases.includes('latest')) {
      info.hasLatest = true;
      info.latest = versionNumber;
    }
    if (
      !defaultFound &&
      defaultJobVersion &&
      aliases.includes(defaultJobVersion)
    ) {
      defaultFound = true;
    }
    info.runsCount += version.usedBy.totalCount;
    const mostRecentRun = getMostRecentRunCreated(version.usedBy);
    if (
      mostRecentRun &&
      (!info.mostRecentRun || mostRecentRun > info.mostRecentRun)
    ) {
      info.mostRecentRun = mostRecentRun;
    }
    if (versionNumbers.length) {
      versionNumbers.sort((a, b) => b - a);
      info.highest = versionNumbers[versionNumbers.length - 1];
      info.runTarget =
        defaultJobVersion && defaultFound
          ? defaultJobVersion
          : info.hasLatest
            ? 'latest'
            : `v${info.highest}`;

      if (defaultJobVersion && defaultFound) {
        info.runTargetNum = versionNumber;
        // found passed in default, return
        return info;
      }
      info.runTargetNum = info.hasLatest ? info.latest : info.highest;
    }
  }
  return info;
}

export function getJobInfo(
  jobEdges: JobEdge[],
  entityName: string,
  projectName: string
): JobInfo[] {
  return jobEdges.map(jobEdge => {
    const {id, name, description, createdAt} = jobEdge.node;
    const artifacts = getArtifactInfo(jobEdge.node.artifacts.edges);
    const versionInfo = getVersionInfo(artifacts);
    return {
      id,
      name,
      description: description ?? '',
      createdAt,
      artifacts,
      versionInfo,
      entityName,
      projectName,
    };
  });
}

export function getRecentlyUsedJobInfo(
  artifactCollectionEdges: ArtifactCollectionEdge[]
): JobInfo[] {
  return artifactCollectionEdges.map(edge => {
    const {id, name, description, createdAt, project, artifacts} = edge.node;
    const artifactInfo = getArtifactInfo(artifacts?.edges ?? ([] as any));
    const versionInfo = getVersionInfo(artifactInfo);

    return {
      id,
      name,
      description: description ?? '',
      createdAt,
      artifacts: artifactInfo,
      versionInfo,
      entityName: project?.entityName ?? '',
      projectName: project?.name ?? '',
    };
  });
}

export function getPublicJobInfo(
  jobEdges: JobEdge[],
  entityName: string,
  projectName: string
): JobInfo[] {
  const publicJobs = jobEdges.filter(jobEdge =>
    isJobValidForPublicQueue(
      WANDB_PUBLIC_QUEUE_NAME,
      jobEdge.node.name,
      projectName,
      entityName
    )
  );

  return getJobInfo(publicJobs, entityName, projectName);
}

export function constructFullJobName(
  entityName: string,
  projectName: string,
  collectionName: string,
  alias?: string
) {
  // This function does not create a path to view the job artifact in the UI
  // The resultant jobName here is consumed by the SDK artifact() method
  // which parses the entityName, projectName and artifactName from this jobName
  const jobName = `${entityName}/${projectName}/${collectionName}`;
  if (alias) {
    return `${jobName}:${alias}`;
  }
  return jobName;
}
